import { useMemo, useState } from 'react';
import { AlertTriangle, Check, Clock3, Receipt, Wallet } from 'lucide-react';

import { Card, CardContent, CardHeader } from '@/ui/base/card';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';

import { useAcompanhamentoMensal } from '../hooks/useApuracao';
import { useCicloFinanceiroPipeline } from '../hooks/useFechamentoV2';
import { PipelineFinanceiro } from '../PipelineFinanceiro';
import type { AcompanhamentoMensalLinha } from '../types';

const ETAPAS_BOOLEANAS: {
  chave: 'etapaCalculada' | 'etapaEmpenho' | 'etapaLiquidada' | 'etapaSof' | 'etapaPaga';
  rotulo: string;
}[] = [
  { chave: 'etapaCalculada', rotulo: 'Calculada' },
  { chave: 'etapaEmpenho', rotulo: 'Empenho' },
  { chave: 'etapaLiquidada', rotulo: 'Liquidada' },
  { chave: 'etapaSof', rotulo: 'SOF' },
  { chave: 'etapaPaga', rotulo: 'Paga' },
];

function CelulaEtapa({ concluida }: { concluida: boolean }) {
  return concluida ? (
    <Check className="mx-auto h-4 w-4 text-success" aria-label="Concluída" />
  ) : (
    <Clock3 className="mx-auto h-4 w-4 text-muted-foreground" aria-label="Pendente" />
  );
}

/** Ciclo por credenciado (apontamento → SME → calculada → empenho → docs →
 * liquidada → SOF → paga) — usada na aba "Acompanhamento Mensal" do hub de
 * Apuração e embutida na aba "Acompanhamento" do Fechamento Mensal V2. */
export function AcompanhamentoMensalTable({ mes }: { mes: string }) {
  const { data, isLoading, isError, refetch } = useAcompanhamentoMensal(mes);
  const pipeline = useCicloFinanceiroPipeline(mes);
  const [busca, definirBusca] = useState('');

  const filtrados = useMemo(() => {
    const base = data ?? [];
    const termo = busca.trim().toLowerCase();
    if (!termo) return base;
    return base.filter(
      (l) =>
        l.credenciadoNome.toLowerCase().includes(termo) || l.cpfCnpj.toLowerCase().includes(termo),
    );
  }, [data, busca]);

  if (isError) {
    return (
      <ErrorState
        variant="load-failed"
        primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
      />
    );
  }

  const smeDevolvidos = filtrados.filter((l) => l.etapaSme === 'DEVOLVIDO').length;
  const docsPendentesTotal = filtrados.reduce((s, l) => s + l.docsPendentes, 0);
  const aProcessar = filtrados.filter((l) => !l.etapaApontamento).length;

  const stats: StatItem[] = [
    { title: 'Medições no mês', value: filtrados.length, icon: Receipt },
    { title: 'A processar', value: aProcessar, icon: Clock3 },
    { title: 'Empenhadas', value: filtrados.filter((l) => l.etapaEmpenho).length, icon: Check },
    { title: 'Pagas', value: filtrados.filter((l) => l.etapaPaga).length, icon: Check },
    {
      title: 'Valor total',
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
        filtrados.reduce((s, l) => s + parseFloat(l.valorTotal), 0),
      ),
      icon: Wallet,
    },
  ];

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por credenciado ou CPF/CNPJ…',
      ariaLabel: 'Buscar credenciado',
    },
  ];

  const colunas: DataTableColumn<AcompanhamentoMensalLinha & { id: string }>[] = [
    {
      header: 'Credenciado',
      cell: (l) => (
        <div>
          <p className="font-medium">{l.credenciadoNome}</p>
          <p className="text-xs text-muted-foreground">{l.cpfCnpj}</p>
        </div>
      ),
    },
    {
      header: 'Valor',
      cell: (l) => <MoneyDisplay value={l.valorTotal} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Apontamento',
      cell: (l) => <CelulaEtapa concluida={l.etapaApontamento} />,
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    },
    {
      header: 'SME',
      cell: (l) =>
        l.etapaSme === 'DEVOLVIDO' ? (
          <AlertTriangle
            className="mx-auto h-4 w-4 text-warning"
            aria-label="Lote devolvido pela SME"
          />
        ) : (
          <CelulaEtapa concluida={l.etapaSme === 'OK'} />
        ),
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    },
    ...ETAPAS_BOOLEANAS.slice(0, 2).map(({ chave, rotulo }) => ({
      header: rotulo,
      cell: (l: AcompanhamentoMensalLinha & { id: string }) => <CelulaEtapa concluida={l[chave]} />,
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    })),
    {
      header: 'Docs',
      cell: (l) =>
        l.docsPendentes > 0 ? (
          <span
            className="inline-flex items-center justify-center gap-1 text-warning"
            title={`${l.docsPendentes} documento(s) pendente(s)`}
          >
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs font-semibold tabular-nums">{l.docsPendentes}</span>
          </span>
        ) : (
          <CelulaEtapa concluida={l.etapaCalculada} />
        ),
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    },
    ...ETAPAS_BOOLEANAS.slice(2).map(({ chave, rotulo }) => ({
      header: rotulo,
      cell: (l: AcompanhamentoMensalLinha & { id: string }) => <CelulaEtapa concluida={l[chave]} />,
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    })),
  ];

  return (
    <div className="space-y-4">
      <FilterBar
        fields={campos}
        values={{ busca }}
        onChange={(_, v) => definirBusca(v)}
        onClear={() => definirBusca('')}
      />

      {pipeline.data && <PipelineFinanceiro dados={pipeline.data} />}

      <StatsGrid stats={stats} columns="grid-cols-2 md:grid-cols-3 lg:grid-cols-5" />

      {smeDevolvidos > 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <strong>Próximo passo:</strong> destravar {smeDevolvidos} medição(ões) com SME
            devolvida. O ciclo financeiro só avança após a re-aprovação.
          </span>
        </div>
      ) : docsPendentesTotal > 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <strong>Próximo passo:</strong> cobrar {docsPendentesTotal} documento(s) pendente(s) nos
            demonstrativos — a coluna Docs mostra quem falta.
          </span>
        </div>
      ) : (
        aProcessar > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            <Clock3 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              <strong className="text-foreground">Próximo passo:</strong> {aProcessar} medição(ões)
              ainda aguardando apontamento.
            </span>
          </div>
        )
      )}

      <Card>
        <CardHeader className="pb-0">
          <p className="text-base font-semibold">Ciclo por credenciado</p>
          <p className="text-sm text-muted-foreground">
            Status de cada etapa para cada medição do mês
          </p>
        </CardHeader>
        <CardContent className="p-0 pt-4">
          <DataTable
            columns={colunas as DataTableColumn<AcompanhamentoMensalLinha & { id: string }>[]}
            data={filtrados.map((l) => ({ ...l, id: l.credenciadoId }))}
            isLoading={isLoading}
            emptyTitle="Nenhuma medição encontrada"
            emptyDescription="Não há credenciados com ciclo iniciado nesta competência."
            ariaLabel="Ciclo por credenciado"
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default AcompanhamentoMensalTable;
